import { BotonicPackageNames } from '@hubtype/data-access-models'

import { useFlowBuilderSelector } from '../reducer/hooks'

export function useBotConfig(): {
  isBotActionConnectable: boolean
  isPreviewEnabled: boolean
  isQueuePositionChangedEnabled: boolean
  isKnowledgeBaseSettingsEnabled: boolean
} {
  const { botConfig, organizationContents } = useFlowBuilderSelector(
    ctx => ctx.state
  )

  const isAllowedToEnableQueuePositionChanged = (): boolean => {
    if (!botConfig?.packages) return false

    const coreVersion = botConfig.packages[BotonicPackageNames.Core]?.version

    const reactVersion = botConfig.packages[BotonicPackageNames.React]?.version

    const flowBuilderVersion =
      botConfig.packages[BotonicPackageNames.PluginFlowBuilder]?.version

    return (
      isVersionHigherOrEqualThan(coreVersion, '0.33.0') &&
      isVersionHigherOrEqualThan(reactVersion, '0.33.0') &&
      isVersionHigherOrEqualThan(flowBuilderVersion, '0.33.0')
    )
  }

  const isAllowedToEditKnowledgeBaseSettings = (): boolean => {
    if (!botConfig?.packages) return false

    const flowBuilderVersion =
      botConfig.packages[BotonicPackageNames.PluginFlowBuilder]?.version

    return isVersionHigherOrEqualThan(flowBuilderVersion, '0.33.0')
  }

  const isAllowedToConnectBotActions = (): boolean => {
    if (!botConfig?.packages) return false
    const flowBuilderVersion =
      botConfig.packages[BotonicPackageNames.PluginFlowBuilder]?.version

    return isVersionHigherOrEqualThan(flowBuilderVersion, '0.30.0')
  }

  const isPreviewEnabled = (): boolean => {
    const isPreviewEnabledByFeatureFlag =
      organizationContents.currentBot?.flowBuilderSettings?.isPreviewEnabled

    if (isPreviewEnabledByFeatureFlag) return true

    if (!botConfig?.packages) return false

    const reactVersion = botConfig.packages[BotonicPackageNames.React]?.version

    return isVersionHigherOrEqualThan(reactVersion, '0.28.0')
  }

  return {
    isBotActionConnectable: isAllowedToConnectBotActions(),
    isPreviewEnabled: isPreviewEnabled(),
    isQueuePositionChangedEnabled: isAllowedToEnableQueuePositionChanged(),
    isKnowledgeBaseSettingsEnabled: isAllowedToEditKnowledgeBaseSettings(),
  }
}

function isVersionHigherOrEqualThan(
  version: string | undefined,
  targetVersion: string
): boolean {
  if (!version) return false

  const currentParsedVersion = parseVersion(version)
  const targetParsedVersion = parseVersion(targetVersion)

  if (currentParsedVersion.major !== targetParsedVersion.major) {
    return currentParsedVersion.major > targetParsedVersion.major
  }
  if (currentParsedVersion.minor !== targetParsedVersion.minor) {
    return currentParsedVersion.minor > targetParsedVersion.minor
  }
  return currentParsedVersion.patch >= targetParsedVersion.patch
}

function parseVersion(version: string) {
  const [main, ...suffix] = version?.split('-') ?? []
  const [major, minor, patch] = main?.split('.').map(Number) ?? []
  return { major, minor, patch, suffix: suffix.join('-') }
}
